import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, type TokenPair } from "@evocha/api-client";
import { adminApi, ADMIN_STORAGE_KEY } from "./api";

export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

export interface AdminAuthSession extends TokenPair {
  admin: AdminUser;
}

interface StoredSession extends TokenPair {
  admin?: AdminUser;
}

interface SessionValue {
  admin: AdminUser | null;
  authenticated: boolean;
  loading: boolean;
  signIn: (session: AdminAuthSession) => void;
  signOut: () => void;
}

const SessionContext = createContext<SessionValue | null>(null);

function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(ADMIN_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    return parsed?.access_token ? parsed : null;
  } catch {
    return null;
  }
}

function saveSession(session: StoredSession | null) {
  try {
    if (session) localStorage.setItem(ADMIN_STORAGE_KEY, JSON.stringify(session));
    else localStorage.removeItem(ADMIN_STORAGE_KEY);
  } catch {
    /* private mode */
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<StoredSession | null>(() => loadSession());
  const [loading, setLoading] = useState<boolean>(() => {
    const stored = loadSession();
    return Boolean(stored?.access_token && !stored.admin);
  });

  useEffect(() => {
    const stored = loadSession();
    if (!stored?.access_token) {
      setLoading(false);
      return;
    }
    if (stored.admin) {
      setLoading(false);
      return;
    }
    let alive = true;
    adminApi
      .get<{ admin: AdminUser }>("/admin/v1/auth/me")
      .then((me) => {
        if (!alive) return;
        const next: StoredSession = { ...stored, admin: me.admin };
        saveSession(next);
        setSession(next);
      })
      .catch((err: unknown) => {
        if (!alive) return;
        // Transport or server errors must not destroy a valid stored session.
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
          saveSession(null);
          setSession(null);
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const signIn = useCallback(
    (next: AdminAuthSession) => {
      saveSession({
        access_token: next.access_token,
        refresh_token: next.refresh_token,
        token_type: next.token_type,
        expires_in: next.expires_in,
        refresh_expires_in: next.refresh_expires_in,
        admin: next.admin,
      });
      setSession({
        access_token: next.access_token,
        refresh_token: next.refresh_token,
        token_type: next.token_type,
        expires_in: next.expires_in,
        refresh_expires_in: next.refresh_expires_in,
        admin: next.admin,
      });
      setLoading(false);
      queryClient.clear();
    },
    [queryClient],
  );

  const signOut = useCallback(() => {
    const stored = loadSession();
    void adminApi
      .post("/admin/v1/auth/logout", { refresh_token: stored?.refresh_token })
      .catch(() => undefined);
    saveSession(null);
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  const value = useMemo<SessionValue>(
    () => ({
      admin: session?.admin ?? null,
      authenticated: Boolean(session?.access_token),
      loading,
      signIn,
      signOut,
    }),
    [session, loading, signIn, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useAdminSession(): SessionValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useAdminSession must be used inside SessionProvider");
  return ctx;
}
